/* ===========================================================================
   woolberry — product-page accordions: one open at a time, one clean motion.

   One open at a time (2026-09-26): the product section's accordion blocks
   (Size Guide, Product Details, Washing & Care, Delivery & Returns) each
   render inside their OWN <collapsible-elements single>
   (snippets/product-tabs.liquid), so the theme's single mode never sees a
   sibling. This listens for the theme's bubbling toggle event and, when a
   row has just opened, closes the other open rows in the same section.
   Both breakpoints.

   No jump, no jitter (user, 2026-09-27, second pass — the first version
   re-scrolled every animation frame to chase the theme's collapse and
   "felt jumpy and buggy"): everything that could move the tapped row is
   settled SYNCHRONOUSLY inside the toggle event, before the browser paints.
     - An open row ABOVE the tapped one is closed instantly (transition
       suppressed) and the page is scrolled by exactly the height that
       vanished, in the same frame. The tapped title therefore never moves a
       pixel; the shopper sees the row above fold away and the tapped
       content unfold. A row BELOW closes with the theme's animation, since
       it cannot move the tapped title.
     - If the opened row would not be fully in view (title under the fixed
       banner+header, content above the sticky Shop now bar), the page
       glides so the title sits 16px under the header — ONE scroll
       animation, driven here, 300ms on the theme's own `ease` curve
       (cubic-bezier .25 .1 .25 1), started together with the theme's 0.3s
       open animation so the two read as a single movement. Fully in view →
       nothing moves.
   Any wheel / touch / key input cancels the glide; the shopper is never
   fought for the scrollbar. The theme's html { scroll-behavior: smooth }
   is switched off inline while this scrolls, or its own smoothing would
   lag every step.

   The "Size guide" link (assets/wb-sizechart.js) opens its row by clicking
   the summary and leaves the scrolling to this file when it is present
   (window.wbAccordionSingle).

   Scoped to .product__block--accordion (product rows AND the FAQ rows carry
   it), so the mobile menu, cart blocks and filters are untouched. For FAQ
   rows the theme's single mode also schedules closes; its close() bails on
   a row that is already shut, so the two never collide.
   Loaded from layout/theme.liquid. Testing trap: requestAnimationFrame
   never fires while the desktop app's Browser pane is hidden — shim it.
   =========================================================================== */
(function () {
  'use strict';

  var SEL = '.product__block--accordion [data-collapsible]';
  var GAP = 16;
  var DURATION = 300; // theme.css: [data-collapsible-body] { transition: height 0.3s ease }
  var html = document.documentElement;
  var anim = null;

  /* CSS `ease` = cubic-bezier(0.25, 0.1, 0.25, 1), solved for y at a given x */
  function ease(x) {
    var p1x = 0.25, p1y = 0.1, p2x = 0.25, p2y = 1;
    function bez(t, a, b) { var u = 1 - t; return 3 * u * u * t * a + 3 * u * t * t * b + t * t * t; }
    var t = x;
    for (var i = 0; i < 6; i++) {
      var err = bez(t, p1x, p2x) - x;
      if (Math.abs(err) < 1e-4) break;
      var u = 1 - t;
      var d = 3 * u * u * p1x + 6 * u * t * (p2x - p1x) + 3 * t * t * (1 - p2x);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
      if (t < 0) t = 0; else if (t > 1) t = 1;
    }
    return bez(t, p1y, p2y);
  }

  function headerOffset() {
    var g = document.querySelector('.header-sections');
    return (g ? g.offsetHeight : 0) + GAP;
  }

  function footRoom() {
    var bar = document.querySelector('cart-bar.is-visible');
    return bar ? bar.offsetHeight : 0;
  }

  function cancelAnim() {
    if (!anim) return;
    anim.cancelled = true;
    html.style.scrollBehavior = anim.prevBehavior;
    anim = null;
  }
  ['wheel', 'touchstart', 'keydown'].forEach(function (type) {
    document.addEventListener(type, cancelAnim, { passive: true });
  });

  /* what the theme's close() leaves behind once its animation has ended,
     applied in one go with the transition suppressed */
  function closeInstant(row) {
    var body = row.querySelector('[data-collapsible-body]');
    var prev = body ? body.style.transition : '';
    if (body) {
      body.style.transition = 'none';
      body.style.height = '';
    }
    row.removeAttribute('open');
    if (body) {
      void body.offsetHeight; // commit the 0 height before the transition comes back
      body.style.transition = prev;
    }
  }

  function closeAnimated(row) {
    var host = row.closest('collapsible-elements');
    if (host && typeof host.close === 'function') {
      requestAnimationFrame(function () { host.close(row); });
    } else {
      closeInstant(row);
    }
  }

  function closeOthers(opened) {
    var scope = opened.closest('.shopify-section') || opened.closest('[data-section-id]') || document;
    var host = opened.closest('collapsible-elements');
    var rows = scope.querySelectorAll(SEL);
    var openedTop = opened.getBoundingClientRect().top;
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (row === opened || !row.hasAttribute('open')) continue;
      if (row.getBoundingClientRect().top < openedTop) {
        closeInstant(row); // above: fold away now, the scroll fix below keeps the tapped row still
      } else if (!(host && row.closest('collapsible-elements') === host)) {
        closeAnimated(row); // below, other host: animate; same host: the theme's single mode does it
      }
    }
  }

  function animateScrollTo(to) {
    cancelAnim();
    var from = window.pageYOffset;
    if (Math.abs(to - from) < 1) return;
    var job = { cancelled: false, prevBehavior: html.style.scrollBehavior };
    anim = job;
    html.style.scrollBehavior = 'auto';
    var t0 = performance.now();
    function step(now) {
      if (job.cancelled) return;
      var p = (now - t0) / DURATION;
      if (p >= 1) {
        window.scrollTo(0, to);
        html.style.scrollBehavior = job.prevBehavior;
        anim = null;
        return;
      }
      window.scrollTo(0, from + (to - from) * ease(p < 0 ? 0 : p));
      requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function glideIfNeeded(opened) {
    var top = opened.getBoundingClientRect().top;
    var summary = opened.querySelector('[data-collapsible-trigger]');
    var content = opened.querySelector('[data-collapsible-content]');
    var bottom = top + (summary ? summary.offsetHeight : 0) + (content ? content.offsetHeight : 0) + 24;
    var off = headerOffset();
    if (top >= off && bottom <= window.innerHeight - footRoom()) return; // clearly in view already

    var target = window.pageYOffset + top - off;
    var max = Math.max(0, html.scrollHeight - window.innerHeight);
    if (target > max) target = max;
    if (target < 0) target = 0;
    animateScrollTo(Math.round(target));
  }

  document.addEventListener('theme:collapsible:toggle', function (e) {
    var opened = e.target && e.target.closest ? e.target.closest(SEL) : null;
    if (!opened || opened.getAttribute('open') !== 'true') return; // a close, or not a PDP accordion

    cancelAnim();
    var before = opened.getBoundingClientRect().top;
    closeOthers(opened);
    var drift = opened.getBoundingClientRect().top - before; // rows above folded: the page got shorter
    if (Math.abs(drift) > 0.5) {
      var prev = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollBy(0, drift); // same frame as the fold: the tapped title stays put
      html.style.scrollBehavior = prev;
    }
    glideIfNeeded(opened);
  });

  window.wbAccordionSingle = true;
})();
